<script setup lang="ts">
import {
  absMoney,
  formatDate,
  formatMonthYear,
  isZeroMoney,
  type Locale,
  type LocalDate,
  type Money,
  moneyProportion,
  zeroMoney,
} from '@taler/domain'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AccountFlowPeriod as FlowPeriodFigures } from '../internal/types.ts'

interface Bar {
  readonly key: string
  readonly label: string
  readonly net: Money
  readonly width: number
}

const props = defineProps<{
  periods: readonly FlowPeriodFigures[]
  locale: Locale
  /** A month bucket reads as a month name; a year bucket as the year. */
  granularity: 'MONTH' | 'YEAR'
}>()

const { t } = useI18n()

function labelOf(start: LocalDate): string {
  return props.granularity === 'YEAR'
    ? formatDate(start, props.locale, 'iso').slice(0, 4)
    : formatMonthYear(start, props.locale)
}

const bars = computed<Bar[]>(() => {
  const magnitudes = props.periods.map((period) => absMoney(period.net))
  const largest = magnitudes.reduce<Money | undefined>(
    (max, value) => (max === undefined || value.units > max.units ? value : max),
    undefined,
  )
  return props.periods.map((period, index) => ({
    key: formatDate(period.periodStart, props.locale, 'iso'),
    label: labelOf(period.periodStart),
    net: period.net,
    width:
      largest === undefined || isZeroMoney(largest)
        ? 0
        : moneyProportion(magnitudes[index]!, zeroMoney(largest.currency), largest),
  }))
})
</script>

<template>
  <figure class="flex flex-col gap-[var(--space-inline)]">
    <figcaption class="text-[length:var(--text-step-2)] font-semibold text-[color:var(--color-text)]">
      {{ t('accountDetail.byPeriodTitle') }}
    </figcaption>
    <ol class="flex flex-col gap-[var(--space-inline)]">
      <li
        v-for="bar in bars"
        :key="bar.key"
        class="grid grid-cols-[minmax(7rem,10rem)_1fr_auto] items-center gap-[var(--space-group)] text-[length:var(--text-step-2)]"
      >
        <span class="truncate text-[color:var(--color-text-muted)]">{{ bar.label }}</span>
        <span
          class="h-3 rounded-[var(--radius-control)] bg-[color:var(--color-surface-sunken)]"
          aria-hidden="true"
        >
          <span
            class="block h-full rounded-[var(--radius-control)]"
            :class="
              bar.net.units < 0n ? 'bg-[color:var(--color-negative)]' : 'bg-[color:var(--color-positive)]'
            "
            :style="{ width: `${Math.max(bar.width * 100, bar.net.units === 0n ? 0 : 1)}%` }"
          />
        </span>
        <MoneyAmount :value="bar.net" sign :tone="bar.net.units < 0n ? 'negative' : 'positive'" />
      </li>
    </ol>
  </figure>
</template>
